import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { notifyError } from '@/lib/query'
import { bulkDelete, deleteRow } from '@/lib/resources'
import type { ResourceSchema } from '@/lib/resources'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  schema: ResourceSchema
  /** Set of row ids — single delete passes one, bulk passes many. */
  ids: Array<string | number>
  onDeleted?: (count: number) => void
}

/** Typed-confirmation gate: bulk OR more than one item requires DELETE-typed confirmation. */
const TYPED_CONFIRMATION_THRESHOLD = 2

export function ResourceDeleteDialog({
  open,
  onOpenChange,
  schema,
  ids,
  onDeleted
}: Props) {
  const [submitting, setSubmitting] = useState(false)
  const [typed, setTyped] = useState('')

  const count = ids.length
  const requiresTyped = count >= TYPED_CONFIRMATION_THRESHOLD
  const canSubmit = !requiresTyped || typed === 'DELETE'

  async function handleConfirm() {
    setSubmitting(true)
    try {
      if (count === 1) {
        await deleteRow(schema.key, ids[0])
      } else {
        await bulkDelete(schema.key, ids)
      }
      toast.success(
        count === 1
          ? `${schema.singular} deleted`
          : `${count} ${schema.label.toLowerCase()} deleted`
      )
      onDeleted?.(count)
      onOpenChange(false)
      setTyped('')
    } catch (err) {
      notifyError('Could not delete', err)
      setTyped('')
    } finally {
      setSubmitting(false)
    }
  }

  const description =
    count === 1
      ? `This will permanently delete this ${schema.singular.toLowerCase()}. ${schema.soft_delete ? 'It will be marked deleted but kept for recovery.' : 'This cannot be undone.'}`
      : `This will permanently delete ${count} ${schema.label.toLowerCase()}. ${schema.soft_delete ? 'They will be marked deleted but kept for recovery.' : 'This cannot be undone.'}`

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (!submitting) onOpenChange(o)
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-destructive" />
            Delete{' '}
            {count === 1
              ? schema.singular.toLowerCase()
              : `${count} ${schema.label.toLowerCase()}`}
            ?
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {requiresTyped && (
          <div className="grid gap-2">
            <Label htmlFor="delete-confirm" className="text-sm">
              Type <span className="font-semibold">DELETE</span> to confirm
            </Label>
            <Input
              id="delete-confirm"
              autoComplete="off"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="DELETE"
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={submitting || !canSubmit}
            onClick={handleConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {submitting && <Loader2 className="size-4 animate-spin" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
